import { motion } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import React from 'react';

import { EASE_OUT, SABER_BLUE, SABER_GREEN, SPACE_BLACK, STAR_GRAY, TEXT_LIGHT } from '../constants';
import { HoloCard } from '../ui-helpers';

const BRIDGES = [
  { name: 'Coruscant Relay', toBridge: '2ms', toServer: '52ms', toBridgeMs: 2, toServerMs: 52, totalMs: 54, color: SABER_BLUE },
  { name: 'Naboo Gateway', toBridge: '48ms', toServer: '1ms', toBridgeMs: 48, toServerMs: 1, totalMs: 49, color: SABER_BLUE },
  { name: 'Tatooine Hub', toBridge: '30ms', toServer: '12ms', toBridgeMs: 30, toServerMs: 12, totalMs: 42, color: SABER_GREEN },
  { name: 'Kamino Link', toBridge: '38ms', toServer: '5ms', toBridgeMs: 38, toServerMs: 5, totalMs: 43, color: SABER_BLUE },
  { name: 'Endor Station', toBridge: '1ms', toServer: '48ms', toBridgeMs: 1, toServerMs: 48, totalMs: 49, color: SABER_BLUE },
] as const;

const W = 700;
const H = 200;
const USER_X = 40;
const MERGE_X = 540;
const SERVER_X = 660;
const CENTER_Y = H / 2;
const BRIDGE_X = 290;
const LANE_YS = [22, 58, CENTER_Y, H - 58, H - 22];
const MS_TO_SEC = 0.1;
const BEST_MS = Math.min(...BRIDGES.map((b) => b.totalMs));
const WORST_TOTAL_SEC = Math.max(...BRIDGES.map((b) => b.totalMs)) * MS_TO_SEC;
const PACKETS_PER_BRIDGE = 5;
const PACKET_INTERVAL = WORST_TOTAL_SEC / PACKETS_PER_BRIDGE;

export function HyperspaceRouting() {
  const { t } = useTranslation(['components/trial/lp/v4b']);

  return (
    <div className='w-full'>
      <HoloCard className='p-3 md:p-5' glow={SABER_BLUE}>
        <div className='mb-2 flex items-center justify-between'>
          <span className='text-[10px] font-semibold uppercase tracking-widest md:text-xs' style={{ color: SABER_BLUE, fontFamily: 'var(--font-outfit)' }}>
            {t('hyperspace.nav_label')}
          </span>
          <div className='flex items-center gap-2'>
            <motion.div className='size-1.5 rounded-full' style={{ backgroundColor: SABER_GREEN }} animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' as const }} />
            <span className='rounded border px-2 py-0.5 text-[10px] font-bold' style={{ color: SABER_GREEN, borderColor: `${SABER_GREEN}40`, backgroundColor: `${SABER_GREEN}08`, fontFamily: 'var(--font-outfit)' }}>
              {t('hyperspace.lanes_active')}
            </span>
          </div>
        </div>

        <div className='overflow-hidden rounded-lg' style={{ backgroundColor: SPACE_BLACK, border: `1px solid ${SABER_BLUE}10` }}>
          <svg viewBox={`0 0 ${W} ${H}`} className='w-full' preserveAspectRatio='xMidYMid meet'>
            <defs>
              <filter id='sw-pktGlow'>
                <feGaussianBlur stdDeviation='3' result='b' />
                <feMerge>
                  <feMergeNode in='b' />
                  <feMergeNode in='b' />
                  <feMergeNode in='SourceGraphic' />
                </feMerge>
              </filter>
              <filter id='sw-iconGlow'>
                <feGaussianBlur stdDeviation='2' result='b' />
                <feMerge>
                  <feMergeNode in='b' />
                  <feMergeNode in='SourceGraphic' />
                </feMerge>
              </filter>
            </defs>

            <g>
              <circle cx={USER_X} cy={CENTER_Y} r='18' fill={`${SABER_BLUE}20`} stroke={SABER_BLUE} strokeWidth='1.5' />
              <text x={USER_X} y={CENTER_Y + 4} textAnchor='middle' fill={SABER_BLUE} fontSize='10' fontWeight='bold'>
                {t('hyperspace.you')}
              </text>
              <text x={USER_X} y={CENTER_Y + 32} textAnchor='middle' fill={STAR_GRAY} fontSize='7'>
                {t('hyperspace.your_ship')}
              </text>
            </g>

            <g filter='url(#sw-iconGlow)'>
              <circle cx={SERVER_X} cy={CENTER_Y} r='18' fill={`${SABER_GREEN}20`} stroke={SABER_GREEN} strokeWidth='1.5' />
              <text x={SERVER_X} y={CENTER_Y + 4} textAnchor='middle' fill={SABER_GREEN} fontSize='9' fontWeight='bold'>
                {t('hyperspace.server')}
              </text>
              <text x={SERVER_X} y={CENTER_Y + 32} textAnchor='middle' fill={STAR_GRAY} fontSize='7'>
                {t('hyperspace.game_server')}
              </text>
            </g>

            <line x1={USER_X + 20} y1={CENTER_Y} x2={USER_X + 40} y2={CENTER_Y} stroke={SABER_GREEN} strokeWidth='1.5' opacity='0.5' />
            <line x1={MERGE_X + 10} y1={CENTER_Y} x2={SERVER_X - 20} y2={CENTER_Y} stroke={SABER_GREEN} strokeWidth='1.5' opacity='0.6' />

            <g>
              <rect x={MERGE_X - 8} y={CENTER_Y - 10} width='16' height='20' rx='2' fill={`${SABER_GREEN}12`} stroke={SABER_GREEN} strokeWidth='0.8' />
              <circle cx={MERGE_X - 4} cy={CENTER_Y - 5} r='0.8' fill={SABER_GREEN} />
              <circle cx={MERGE_X - 4} cy={CENTER_Y + 1} r='0.8' fill={SABER_GREEN} opacity='0.7' />
              <circle cx={MERGE_X - 4} cy={CENTER_Y + 7} r='0.8' fill={SABER_GREEN} opacity='0.5' />
            </g>

            {BRIDGES.map((bridge, i) => {
              const ly = LANE_YS[i] ?? CENTER_Y;
              const isBest = bridge.totalMs === BEST_MS;
              const forkX = USER_X + 40;
              const pathD = `M ${USER_X + 20} ${CENTER_Y} L ${forkX} ${CENTER_Y} L ${forkX + 18} ${ly} L ${BRIDGE_X - 18} ${ly}`;
              const pathD2 = `M ${BRIDGE_X + 18} ${ly} L ${MERGE_X - 28} ${ly} L ${MERGE_X - 10} ${CENTER_Y}`;
              const leg1Dur = Math.max(bridge.toBridgeMs * MS_TO_SEC, 0.45);
              const leg2Dur = Math.max(bridge.toServerMs * MS_TO_SEC, 0.45);

              return (
                <g key={`bridge-${i}`}>
                  <motion.path d={pathD} fill='none' stroke={bridge.color} strokeWidth={isBest ? 1.5 : 0.8} strokeOpacity={isBest ? 0.6 : 0.25} strokeDasharray={isBest ? 'none' : '4 3'} initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.7, delay: 0.08 * i, ease: EASE_OUT }} />
                  <motion.path d={pathD2} fill='none' stroke={bridge.color} strokeWidth={isBest ? 1.5 : 0.8} strokeOpacity={isBest ? 0.6 : 0.25} strokeDasharray={isBest ? 'none' : '4 3'} initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.5, delay: 0.08 * i + 0.4, ease: EASE_OUT }} />

                  <rect x={BRIDGE_X - 10} y={ly - 7} width='20' height='14' rx='2' fill={`${bridge.color}10`} stroke={bridge.color} strokeWidth='0.6' strokeOpacity='0.5' />
                  <circle cx={BRIDGE_X - 6} cy={ly - 4} r='0.7' fill={bridge.color} opacity='0.8' />
                  <circle cx={BRIDGE_X - 6} cy={ly + 1} r='0.7' fill={bridge.color} opacity='0.5' />

                  <text x={BRIDGE_X} y={ly - 11} textAnchor='middle' fill={bridge.color} fontSize='5.5' fontFamily='var(--font-outfit)' fontWeight='600' opacity='0.7' letterSpacing='0.3'>
                    {bridge.name.toUpperCase()}
                  </text>

                  <rect x={forkX + 22} y={ly - 5.5} width='24' height='11' rx='5.5' fill={`${bridge.color}15`} stroke={bridge.color} strokeWidth='0.4' strokeOpacity='0.4' />
                  <text x={forkX + 34} y={ly + 2} textAnchor='middle' fill={bridge.color} fontSize='5.5' fontFamily='var(--font-outfit)' fontWeight='700'>
                    {bridge.toBridge.toUpperCase()}
                  </text>

                  <rect x={BRIDGE_X + 24} y={ly - 5.5} width='24' height='11' rx='5.5' fill={`${bridge.color}15`} stroke={bridge.color} strokeWidth='0.4' strokeOpacity='0.4' />
                  <text x={BRIDGE_X + 36} y={ly + 2} textAnchor='middle' fill={bridge.color} fontSize='5.5' fontFamily='var(--font-outfit)' fontWeight='700'>
                    {bridge.toServer.toUpperCase()}
                  </text>

                  <rect x={MERGE_X - 44} y={ly - 5} width='30' height='10' rx='5' fill={`${bridge.color}12`} stroke={bridge.color} strokeWidth='0.4' strokeOpacity='0.3' />
                  <text x={MERGE_X - 29} y={ly + 2} textAnchor='middle' fill={bridge.color} fontSize='5' fontFamily='var(--font-outfit)' fontWeight='700'>{`\u03A3 ${bridge.totalMs}MS`}</text>

                  {Array.from({ length: PACKETS_PER_BRIDGE }).map((_, pIdx) => {
                    const pDelay = 1.5 + pIdx * PACKET_INTERVAL;
                    const fullCycle = PACKETS_PER_BRIDGE * PACKET_INTERVAL + WORST_TOTAL_SEC;
                    return (
                      <g key={`pkt-${i}-${pIdx}`}>
                        <circle r={isBest ? 3.5 : 2.5} fill={bridge.color} filter='url(#sw-pktGlow)'>
                          <animateMotion dur={`${fullCycle}s`} begin={`${pDelay}s`} repeatCount='indefinite' path={pathD} keyTimes={`0;${leg1Dur / fullCycle};1`} keyPoints='0;1;1' />
                        </circle>
                        <circle r={isBest ? 3.5 : 2.5} fill={bridge.color} filter='url(#sw-pktGlow)'>
                          <animateMotion dur={`${fullCycle}s`} begin={`${pDelay + leg1Dur}s`} repeatCount='indefinite' path={pathD2} keyTimes={`0;${leg2Dur / fullCycle};1`} keyPoints='0;1;1' />
                        </circle>
                      </g>
                    );
                  })}
                </g>
              );
            })}
          </svg>
        </div>

        <div className='mt-3 grid grid-cols-3 gap-2'>
          {[
            { value: '5', labelKey: 'hyperspace.stat_bridges', descKey: 'hyperspace.stat_bridges_desc' },
            { value: '5', labelKey: 'hyperspace.stat_hops', descKey: 'hyperspace.stat_hops_desc' },
            { value: '32Q+', labelKey: 'hyperspace.stat_routes', descKey: 'hyperspace.stat_routes_desc' },
          ].map((s, i) => (
            <div key={i} className='rounded-lg p-3 text-center' style={{ backgroundColor: `${SABER_BLUE}06`, border: `1px solid ${SABER_BLUE}10` }}>
              <p className='text-xl font-bold md:text-2xl' style={{ color: SABER_GREEN, fontFamily: 'var(--font-bai)' }}>
                {s.value}
              </p>
              <p className='mt-1 text-sm font-semibold' style={{ color: TEXT_LIGHT, fontFamily: 'var(--font-outfit)' }}>
                {t(s.labelKey)}
              </p>
              <p className='mt-0.5 text-xs' style={{ color: STAR_GRAY }}>
                {t(s.descKey)}
              </p>
            </div>
          ))}
        </div>
      </HoloCard>
    </div>
  );
}
